/**
 * Mises à jour WhatsApp au destinataire d'une course — lien `wa.me`, SANS API.
 *
 * Le bouton « Prévenir le destinataire sur WhatsApp » ouvre WhatsApp (appli ou web)
 * avec un message PRÉREMPLI que la personne relit et envoie elle-même : rien ne part
 * sans un geste humain, et aucun secret n'est nécessaire. L'envoi automatique est
 * un autre chantier (edge function `_shared/whatsapp.ts`, API Cloud de Meta,
 * désactivé tant que ses secrets sont absents).
 *
 * Qui est prévenu (0028) : le DESTINATAIRE de la course s'il a un téléphone, sinon le client
 * qui a commandé — même règle que l'envoi automatique (`choisirCible`). Quand c'est le
 * destinataire et que son nom est connu, le message commence par « Bonjour <nom>, ».
 *
 * ⚠️ TROIS copies de la même logique, à garder identiques :
 *   • ici (console opérateur) ;
 *   • `web/webapp-ts/src/api/whatsapp.ts` (portail SaaS) ;
 *   • `supabase/functions/_shared/whatsapp.ts` (normalisation des numéros + modèles Meta).
 * `whatsapp.test.ts` vérifie que les textes des statuts « prise en charge », « en
 * route » et « livrée » sont mot pour mot ceux des modèles envoyés par l'edge function.
 */
import type { Statut } from './types'
import { cibleAppel } from './destinataire'

/** Numéro maliens par défaut : indicatif ajouté aux numéros locaux à 8 chiffres. */
export const INDICATIF_DEFAUT = '223'

/**
 * Numéro au format international SANS « + » (celui de `wa.me`), ou `null` si on ne
 * peut pas l'établir sans deviner.
 *
 *  • « +223 75 08 36 64 », « 00223 75 08 36 64 » → 22375083664
 *  • « 75 08 36 64 » (8 chiffres, local) → indicatif par défaut (223)
 *  • « 223 75 08 36 64 » → inchangé
 *  • 9 à 15 chiffres sans « + » ni « 00 », ne commençant pas par 0 → pris pour international
 *  • « 06 12 34 56 78 » → refusé : de quel pays ?
 */
export function normaliserNumero(brut: string | null | undefined, paysDefaut: string = INDICATIF_DEFAUT): string | null {
  if (!brut) return null
  const texte = String(brut).trim()
  if (!texte) return null
  const international = texte.startsWith('+')
  const chiffres = texte.replace(/\D+/g, '')
  if (!chiffres) return null

  let resolu: string | null = null
  if (international) resolu = chiffres
  else if (chiffres.startsWith('00')) resolu = chiffres.slice(2)
  else if (chiffres.length === 8) resolu = paysDefaut + chiffres
  else if (chiffres.startsWith(paysDefaut) && chiffres.length === paysDefaut.length + 8) resolu = chiffres
  else if (chiffres.length >= 9 && !chiffres.startsWith('0')) resolu = chiffres
  if (!resolu) return null
  // E.164 : 8 à 15 chiffres, jamais d'indicatif commençant par 0.
  if (resolu.length < 8 || resolu.length > 15 || resolu.startsWith('0')) return null
  return resolu
}

export interface ContexteMessage {
  /** Référence de la course (CMD-…). */
  ref: string
  /** Nom sous lequel le client final connaît l'expéditeur (marque blanche : le nom de l'entreprise). */
  marque: string
  /** Nom du destinataire à saluer (« Bonjour Awa, … »), seulement si c'est LUI qu'on prévient. */
  nom?: string | null
  /** Code de remise, s'il y en a un : ajouté au message « en route » pour que le destinataire le tienne prêt. */
  codeRemise?: string | null
}

/**
 * Messages par statut, en français — la SEULE source de ces textes dans le code
 * des consoles. Prise en charge / en route / livrée reprennent mot pour mot les
 * modèles que l'envoi automatique soumet à Meta.
 */
export function messageDestinataire(statut: Statut, c: ContexteMessage): string {
  const ref = c.ref
  const marque = c.marque
  const bonjour = nomPourSalutation(c.nom) ? `Bonjour ${nomPourSalutation(c.nom)},` : 'Bonjour,'
  switch (statut) {
    case 'creee':
      return `${bonjour} votre commande (réf. ${ref}) est bien enregistrée chez ${marque}. Nous vous préviendrons dès qu'elle sera prise en charge.`
    case 'assignee':
      return `${bonjour} votre colis (réf. ${ref}) a été pris en charge par ${marque}. Un livreur lui est affecté, nous vous préviendrons à chaque étape de la livraison.`
    case 'collecte':
      return `${bonjour} votre colis (réf. ${ref}) a été pris en charge par ${marque}. Nous vous préviendrons à chaque étape de la livraison.`
    case 'en_livraison': {
      const base = `${bonjour} votre colis (réf. ${ref}) est en route vers vous. Livraison assurée par ${marque}. Merci de rester joignable.`
      return c.codeRemise ? `${base} Code de remise à donner au livreur : ${c.codeRemise}.` : base
    }
    case 'livree':
      return `${bonjour} votre colis (réf. ${ref}) a été livré par ${marque}. Merci de votre confiance.`
    case 'annulee':
      return `${bonjour} la livraison de votre colis (réf. ${ref}) a été annulée. Contactez ${marque} pour plus d'informations.`
  }
}

/** Même nettoyage que l'edge function (`nettoyerParametre`) : pas de saut de ligne, espaces simples, 120 caractères. */
function nomPourSalutation(nom: string | null | undefined): string {
  return String(nom ?? '').replace(/[\r\n\t]+/g, ' ').replace(/ {2,}/g, ' ').trim().slice(0, 120)
}

/**
 * Qui prévenir pour une course : numéros candidats et nom à saluer.
 *  • téléphone du destinataire présent → LUI seul, avec son nom s'il est connu. Jamais de repli
 *    sur le client si ce numéro est inexploitable : le message est écrit pour le destinataire ;
 *  • sinon le client : `client_phone`, puis les numéros de repli (fiche client), sans nom.
 */
export function cibleWhatsApp(
  c: { recipient_name?: string | null; recipient_phone?: string | null; client_phone?: string | null },
  repli: ReadonlyArray<string | null | undefined> = [],
): { telephones: string[]; nom: string | null; destinataire: boolean } {
  const cible = cibleAppel(c)
  if (cible.destinataire && cible.telephone) {
    return { telephones: [cible.telephone], nom: nomPourSalutation(c.recipient_name) || null, destinataire: true }
  }
  return { telephones: [cible.telephone, ...repli].filter((t): t is string => !!t && t.trim() !== ''), nom: null, destinataire: false }
}

/** `https://wa.me/<numéro>?text=<message>` — le message est encodé, jamais concaténé tel quel. */
export function lienWhatsApp(numero: string, texte: string): string {
  return `https://wa.me/${numero}?text=${encodeURIComponent(texte)}`
}

export type PreparationWhatsApp =
  | { ok: true; url: string; numero: string; texte: string }
  | { ok: false; raison: string }

/** Tout ce qu'il faut pour afficher le bouton : lien prêt, ou raison pour laquelle il est désactivé. */
export function preparerWhatsApp(
  statut: Statut,
  telephones: ReadonlyArray<string | null | undefined>,
  contexte: ContexteMessage,
): PreparationWhatsApp {
  const fournis = telephones.filter((t): t is string => !!t && t.trim() !== '')
  if (fournis.length === 0) return { ok: false, raison: "Aucun numéro de téléphone n'est enregistré pour cette course." }
  for (const t of fournis) {
    const numero = normaliserNumero(t)
    if (numero) {
      const texte = messageDestinataire(statut, contexte)
      return { ok: true, url: lienWhatsApp(numero, texte), numero, texte }
    }
  }
  return { ok: false, raison: `Le numéro « ${fournis[0]} » n'est pas exploitable : précisez l'indicatif du pays (ex. +223 75 08 36 64).` }
}
